import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramAirChanges } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { triggerSuccessFeedback, triggerErrorFeedback } from '../../../utils/haptics';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.airChanges.v1';

type Fields = { flowRate: string; roomVolume: string; airChanges: string };
const EMPTY: Fields = { flowRate: '', roomVolume: '', airChanges: '' };

/**
 * Air Changes per Hour — ACH = (Q × 60) / Volume
 */
const AirflowAirChangesScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      triggerErrorFeedback();
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const cfm = toImp(fields.flowRate, 'flow');
    const volume = toImp(fields.roomVolume, 'volume');
    const ac = parseFloat(fields.airChanges);

    if (fields.airChanges === '') {
      if (volume <= 0) {
        setError('Room volume must be greater than zero.');
        triggerErrorFeedback();
        return;
      }
      setSolvedFor('airChanges');
      set({ airChanges: ((cfm * 60) / volume).toFixed(2) });
      triggerSuccessFeedback();
    } else if (fields.flowRate === '') {
      setSolvedFor('flowRate');
      set({ flowRate: fromImp((ac * volume) / 60, 'flow', 1) });
      triggerSuccessFeedback();
    } else if (fields.roomVolume === '') {
      if (ac <= 0) {
        setError('Air changes must be greater than zero.');
        triggerErrorFeedback();
        return;
      }
      setSolvedFor('roomVolume');
      set({ roomVolume: fromImp((cfm * 60) / ac, 'volume', 1) });
      triggerSuccessFeedback();
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.flowRate && { label: 'Flow Rate', value: fields.flowRate, unit: unit('flow'), emphasis: solvedFor === 'flowRate' },
    fields.roomVolume && { label: 'Room Volume', value: fields.roomVolume, unit: unit('volume'), emphasis: solvedFor === 'roomVolume' },
    fields.airChanges && { label: 'Air Changes', value: fields.airChanges, unit: 'per hour', emphasis: solvedFor === 'airChanges' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Air Changes"
      subtitle="Calculate air changes per hour for room ventilation"
      formula={
        <>
          <FormulaLine>ACH = (Q × 60) / Volume</FormulaLine>
          <FormulaNote>
            ACH = Air Changes per Hour, Q = Flow Rate ({unit('flow')}), Volume = Room Volume ({unit('volume')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: ACH = (Q × 60) / Volume"
      diagram={<DiagramAirChanges />}
      outputSlots={{
        cfm: fields.flowRate ? toImp(fields.flowRate, 'flow') : undefined,
        volume_cuft: fields.roomVolume ? toImp(fields.roomVolume, 'volume') : undefined,
        air_changes_per_hour: fields.airChanges ? parseFloat(fields.airChanges) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.flowRate) {
          patch.flowRate = fromImp(values.cfm, 'flow', 1);
        }
        if (values.volume_cuft != null && !fields.roomVolume) {
          patch.roomVolume = fromImp(values.volume_cuft, 'volume', 1);
        }
        if (values.air_changes_per_hour != null && !fields.airChanges) {
          patch.airChanges = values.air_changes_per_hour.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Flow Rate (${unit('flow')})`}
          value={fields.flowRate}
          onChangeText={(v) => set({ flowRate: v })}
          rightText={unit('flow')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Room Volume (${unit('volume')})`}
          value={fields.roomVolume}
          onChangeText={(v) => set({ roomVolume: v })}
          rightText={unit('volume')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Air Changes per Hour"
          value={fields.airChanges}
          onChangeText={(v) => set({ airChanges: v })}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowAirChangesScreen;
